import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/ui/password-input';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, AlertCircle, Loader2, Shield, Building, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    fetchInvite();
  }, [token]);

  const fetchInvite = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/invite/${token}`);
      setInvite(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Convite inválido ou expirado');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/public/invite/${token}/accept`, {
        token,
        password
      });

      setSuccess(true);
      
      // Auto-login
      setTimeout(() => {
        login(response.data.token, response.data.user);
        navigate('/');
      }, 2000);
      
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-white">Carregando convite...</p>
        </div>
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="p-8 max-w-md text-center">
          <AlertCircle size={64} className="text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Convite Inválido</h1>
          <p className="text-slate-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/login')}>
            Ir para Login
          </Button>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="p-8 max-w-md text-center">
          <CheckCircle size={64} className="text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Conta Criada!</h1>
          <p className="text-slate-600 mb-4">
            Redirecionando para o sistema...
          </p>
          <Loader2 size={24} className="animate-spin text-emerald-500 mx-auto" />
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/logo-cerp.png" 
            alt="CERP Brasil NR1" 
            className="h-24 mx-auto mb-4"
          />
          <p className="text-slate-400 text-sm">Controle Empresarial de Riscos Psicossociais</p>
        </div>

        {/* Invite Card */}
        <Card className="p-6">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-slate-900">Você foi convidado!</h1>
            <p className="text-slate-600 mt-2">
              Complete seu cadastro para acessar o sistema
            </p>
          </div>

          {/* Invite Details */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6 space-y-3">
            <div className="flex items-center gap-3">
              <Building size={20} className="text-slate-500" />
              <div>
                <p className="text-xs text-slate-500">Empresa</p>
                <p className="font-semibold">{invite?.company_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail size={20} className="text-slate-500" />
              <div>
                <p className="text-xs text-slate-500">Email</p>
                <p className="font-semibold">{invite?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Shield size={20} className="text-slate-500" />
              <div>
                <p className="text-xs text-slate-500">Perfil de Acesso</p>
                <p className="font-semibold capitalize">{invite?.role}</p>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          {/* Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nome</Label>
              <Input value={invite?.nome} disabled className="bg-slate-50" />
            </div>
            <div>
              <Label>Criar Senha *</Label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                required
                minLength={6}
              />
            </div>
            <div>
              <Label>Confirmar Senha *</Label>
              <PasswordInput
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Digite a senha novamente"
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-emerald-500 hover:bg-emerald-600"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin mr-2" />
                  Criando conta...
                </>
              ) : (
                'Criar Minha Conta'
              )}
            </Button>
          </form>

          <p className="text-center text-xs text-slate-500 mt-4">
            Ao criar sua conta, você concorda com os termos de uso do sistema.
          </p>
        </Card>
      </div>
    </div>
  );
};

export default AcceptInvite;
